'use client'

import { Container } from './Container'
import Image from 'next/image'
import backgroundImage from '@/images/background-features.jpg'
import React, { useMemo, useRef } from 'react'

import {
  motion,
  MotionValue,
  motionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from 'framer-motion'
import { urlFor } from '@/lib/apis/sanity'
import type { HowToSection } from '@/lib/apis/sanity'
import { SanityImageSource } from '@sanity/asset-utils'
import { PortableText } from '@portabletext/react'

type Props = {
  content: HowToSection
}

export default function HowToSection({ content }: Props) {
  const scrollOutPercentages = useMemo(
    () => Array.from({ length: content.steps.length }, () => motionValue(0.0)),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content._id],
  )

  const reversed = [...content.steps]
  reversed.reverse()

  return (
    <section
      className="relative bg-blue-600 bg-cover"
      style={{
        backgroundImage: `url(${backgroundImage.src})`,
      }}
    >
      <Container className="relative py-4 sm:py-8 md:py-11">
        <PortableText
          value={content.title}
          components={{
            block: {
              normal: ({ children }) => (
                <h2 className="max-w-4xl font-display text-3xl font-semibold tracking-tight text-gray-50">
                  {children}
                </h2>
              ),
            },
            marks: {
              em: ({ children }) => (
                <span className="text-red-300">{children}</span>
              ),
            },

            hardBreak: false,
          }}
        />
        <p className="mt-4 max-w-4xl text-lg text-gray-200">
          {content.description}
        </p>
        <div className="my-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="flex flex-col gap-8">
            {content.steps.map(({ title, body, image, imageAlt }, idx) => (
              <StepDescription
                key={idx}
                title={`${idx + 1}. ${title}`}
                body={body}
                image={image}
                imageAlt={imageAlt}
                first={idx === 0}
                last={idx === content.steps.length - 1}
                setScrollOutPercentage={(percentage) => {
                  scrollOutPercentages[idx].set(percentage)
                }}
              />
            ))}
          </div>
          <div className="hidden py-12 lg:block">
            <div className="sticky top-[22%] h-[360px] w-full">
              {reversed.map(({ image, imageAlt }, idx) => (
                <StepImage
                  key={content.steps.length - idx - 1}
                  image={image}
                  imageAlt={imageAlt}
                  scrollOutPercentage={
                    scrollOutPercentages[content.steps.length - idx - 1]
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

type StepProps = {
  title: string
  body: string
  image: SanityImageSource
  imageAlt: string
  first: boolean
  last: boolean
  setScrollOutPercentage: (percentage: number) => void
}

function StepDescription({
  title,
  body,
  image,
  imageAlt,
  first,
  last,
  setScrollOutPercentage,
}: StepProps) {
  const ref = useRef(null)
  const scrollIn = useScroll({
    target: ref,
    offset: ['end end', 'start 0.1'],
  })
  const scrollOut = useScroll({
    target: ref,
    offset: ['start 0.1', 'start start'],
  })

  const opacity = useTransform(() => {
    const scrollInYProgress = scrollIn.scrollYProgress.get()
    const scrollOutYProgress = scrollOut.scrollYProgress.get()

    // Already scrolling out
    if (scrollOutYProgress > 0) {
      if (last) return 1.0

      return 0.4 + (1 - scrollOutYProgress)
    } else if (first) {
      // First one is always visible unless it's being scrolled out
      return 1.0
    } else {
      return 0.4 + scrollInYProgress
    }
  })

  useMotionValueEvent(scrollOut.scrollYProgress, 'change', (value) => {
    if (!last) setScrollOutPercentage(value)
  })

  return (
    <div ref={ref}>
      <motion.div
        className="hidden min-h-96 flex-col justify-center py-40 lg:flex"
        style={{ opacity }}
      >
        <h3 className="font-display text-2xl font-semibold tracking-tight text-white">
          {title}
        </h3>
        <p className="mt-4 text-lg text-gray-200">{body}</p>
      </motion.div>
      <div className="flex flex-col justify-center py-3.5 lg:hidden">
        <h3 className="font-display text-2xl font-semibold tracking-tight text-white">
          {title}
        </h3>
        <p className="mb-8 mt-4 text-lg text-gray-200">{body}</p>
        <Image
          src={urlFor(image).width(900).fit('max').quality(100).url()}
          alt={imageAlt}
          className="aspect-[3/2] w-full rounded-2xl object-cover"
          width={900}
          height={600}
        />
      </div>
    </div>
  )
}

type StepImageProps = {
  image: SanityImageSource
  imageAlt: string
  scrollOutPercentage: MotionValue<number>
}

function StepImage({ image, imageAlt, scrollOutPercentage }: StepImageProps) {
  const opacity = useTransform(scrollOutPercentage, (scrollOutPercentage) => {
    return 1 - scrollOutPercentage
  })

  return (
    <motion.div className="absolute top-0" style={{ opacity }}>
      <Image
        src={urlFor(image).width(900).fit('max').quality(100).url()}
        alt={imageAlt}
        className="aspect-[3/2] w-full rounded-4xl object-cover"
        width={900}
        height={600}
      />
    </motion.div>
  )
}
